import i18next from 'i18next';
import type { PlannerRegion } from '../../types';
import { DEFAULT_PLANNER_REGION } from './seasonality';

export type HomeLocationMode = 'idle' | 'locating' | 'granted' | 'manual' | 'denied';

export interface HomeCoordinates {
  lat: number;
  lon: number;
}

export interface HomeLocationState {
  mode: HomeLocationMode;
  region: PlannerRegion | null;
  source: 'geolocation' | 'manual' | null;
  coordinates: HomeCoordinates | null;
}

export interface ResolvedHomeLocation {
  region: PlannerRegion | null;
  coordinates: HomeCoordinates;
}

interface ReviewedCoastalAnchor extends HomeCoordinates {
  name: string;
  region: PlannerRegion;
}

const STORAGE_KEY = 'home.locationAwarePreview';

const VALID_REGIONS: PlannerRegion[] = [
  'tyrrhenian',
  'adriatic',
  'ionian',
  'sicily_channel',
  'western_med',
  'aegean_cyprus'
];

const REGION_REPRESENTATIVE_COORDINATES: Record<PlannerRegion, HomeCoordinates> = {
  tyrrhenian: { lat: 41.1200, lon: 12.4500 },
  adriatic: { lat: 42.4500, lon: 14.2000 },
  ionian: { lat: 39.9500, lon: 17.9500 },
  sicily_channel: { lat: 36.1500, lon: 13.2000 },
  western_med: { lat: 39.3500, lon: 3.1000 },
  aegean_cyprus: { lat: 35.1500, lon: 27.8000 }
};

// Geolocation is only mapped inside deliberately reviewed coastal catchments.
// A nearby anchor is not enough when two different sea regions are similarly
// plausible; those boundary cases fall back to the manual region picker.
const REVIEWED_COASTAL_ANCHORS: ReviewedCoastalAnchor[] = [
  { name: 'Livorno', region: 'tyrrhenian', lat: 43.5485, lon: 10.3106 },
  { name: 'Grosseto', region: 'tyrrhenian', lat: 42.7635, lon: 11.1124 },
  { name: 'Civitavecchia', region: 'tyrrhenian', lat: 42.0938, lon: 11.7922 },
  { name: 'Rome', region: 'tyrrhenian', lat: 41.9028, lon: 12.4964 },
  { name: 'Naples', region: 'tyrrhenian', lat: 40.8518, lon: 14.2681 },
  { name: 'Salerno', region: 'tyrrhenian', lat: 40.6824, lon: 14.7681 },
  { name: 'Palermo', region: 'tyrrhenian', lat: 38.1157, lon: 13.3615 },
  { name: 'Olbia', region: 'tyrrhenian', lat: 40.9236, lon: 9.4964 },

  { name: 'Trieste', region: 'adriatic', lat: 45.6495, lon: 13.7768 },
  { name: 'Venice', region: 'adriatic', lat: 45.4408, lon: 12.3155 },
  { name: 'Rimini', region: 'adriatic', lat: 44.0678, lon: 12.5695 },
  { name: 'Ancona', region: 'adriatic', lat: 43.6158, lon: 13.5189 },
  { name: 'Pescara', region: 'adriatic', lat: 42.4618, lon: 14.2161 },
  { name: 'Bari', region: 'adriatic', lat: 41.1171, lon: 16.8719 },
  { name: 'Brindisi', region: 'adriatic', lat: 40.6327, lon: 17.9418 },
  { name: 'Zadar', region: 'adriatic', lat: 44.1194, lon: 15.2314 },
  { name: 'Split', region: 'adriatic', lat: 43.5081, lon: 16.4402 },
  { name: 'Dubrovnik', region: 'adriatic', lat: 42.6507, lon: 18.0944 },

  { name: 'Taranto', region: 'ionian', lat: 40.4644, lon: 17.2470 },
  { name: 'Crotone', region: 'ionian', lat: 39.0808, lon: 17.1271 },
  { name: 'Catanzaro Lido', region: 'ionian', lat: 38.8191, lon: 16.6174 },
  { name: 'Reggio Calabria', region: 'ionian', lat: 38.1113, lon: 15.6473 },
  { name: 'Catania', region: 'ionian', lat: 37.5079, lon: 15.0830 },
  { name: 'Syracuse', region: 'ionian', lat: 37.0755, lon: 15.2866 },
  { name: 'Corfu', region: 'ionian', lat: 39.6243, lon: 19.9217 },
  { name: 'Patras', region: 'ionian', lat: 38.2466, lon: 21.7346 },

  { name: 'Trapani', region: 'sicily_channel', lat: 38.0176, lon: 12.5365 },
  { name: 'Marsala', region: 'sicily_channel', lat: 37.7982, lon: 12.4342 },
  { name: 'Agrigento', region: 'sicily_channel', lat: 37.3111, lon: 13.5765 },
  { name: 'Valletta', region: 'sicily_channel', lat: 35.8989, lon: 14.5146 },
  { name: 'Lampedusa', region: 'sicily_channel', lat: 35.5010, lon: 12.6090 },
  { name: 'Tunis', region: 'sicily_channel', lat: 36.8065, lon: 10.1815 },
  { name: 'Sfax', region: 'sicily_channel', lat: 34.7398, lon: 10.7600 },

  { name: 'Barcelona', region: 'western_med', lat: 41.3874, lon: 2.1686 },
  { name: 'Tarragona', region: 'western_med', lat: 41.1189, lon: 1.2445 },
  { name: 'Valencia', region: 'western_med', lat: 39.4699, lon: -0.3763 },
  { name: 'Alicante', region: 'western_med', lat: 38.3452, lon: -0.4810 },
  { name: 'Cartagena', region: 'western_med', lat: 37.6257, lon: -0.9966 },
  { name: 'Almeria', region: 'western_med', lat: 36.8340, lon: -2.4637 },
  { name: 'Malaga', region: 'western_med', lat: 36.7213, lon: -4.4214 },
  { name: 'Palma', region: 'western_med', lat: 39.5696, lon: 2.6502 },
  { name: 'Perpignan', region: 'western_med', lat: 42.6887, lon: 2.8948 },
  { name: 'Marseille', region: 'western_med', lat: 43.2965, lon: 5.3698 },
  { name: 'Nice', region: 'western_med', lat: 43.7102, lon: 7.2620 },
  { name: 'Genoa', region: 'western_med', lat: 44.4056, lon: 8.9463 },
  { name: 'Ajaccio', region: 'western_med', lat: 41.9192, lon: 8.7386 },

  { name: 'Piraeus', region: 'aegean_cyprus', lat: 37.9429, lon: 23.6469 },
  { name: 'Thessaloniki', region: 'aegean_cyprus', lat: 40.6401, lon: 22.9444 },
  { name: 'Volos', region: 'aegean_cyprus', lat: 39.3622, lon: 22.9422 },
  { name: 'Kavala', region: 'aegean_cyprus', lat: 40.9396, lon: 24.4018 },
  { name: 'Heraklion', region: 'aegean_cyprus', lat: 35.3387, lon: 25.1442 },
  { name: 'Rhodes', region: 'aegean_cyprus', lat: 36.4341, lon: 28.2176 },
  { name: 'Izmir', region: 'aegean_cyprus', lat: 38.4237, lon: 27.1428 },
  { name: 'Bodrum', region: 'aegean_cyprus', lat: 37.0344, lon: 27.4305 },
  { name: 'Paphos', region: 'aegean_cyprus', lat: 34.7754, lon: 32.4245 },
  { name: 'Limassol', region: 'aegean_cyprus', lat: 34.7071, lon: 33.0226 },
  { name: 'Larnaca', region: 'aegean_cyprus', lat: 34.9003, lon: 33.6232 }
];

const REVIEWED_COASTAL_RADIUS_KM = 90;
const MIN_REGION_SEPARATION_KM = 25;
const EARTH_RADIUS_KM = 6371;

const degreesToRadians = (value: number): number => value * Math.PI / 180;

const distanceInKm = (left: HomeCoordinates, right: HomeCoordinates): number => {
  const latitudeDelta = degreesToRadians(right.lat - left.lat);
  const longitudeDelta = degreesToRadians(right.lon - left.lon);
  const leftLatitude = degreesToRadians(left.lat);
  const rightLatitude = degreesToRadians(right.lat);
  const haversine = Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(leftLatitude) * Math.cos(rightLatitude) * Math.sin(longitudeDelta / 2) ** 2;
  const clampedHaversine = Math.min(1, Math.max(0, haversine));

  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(clampedHaversine), Math.sqrt(1 - clampedHaversine));
};

const clampRegion = (value: unknown): PlannerRegion | null => {
  if (typeof value === 'string' && VALID_REGIONS.includes(value as PlannerRegion)) {
    return value as PlannerRegion;
  }

  return null;
};

const clampCoordinates = (value: unknown): HomeCoordinates | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const maybe = value as Partial<HomeCoordinates>;
  if (typeof maybe.lat !== 'number' || typeof maybe.lon !== 'number') {
    return null;
  }

  return { lat: maybe.lat, lon: maybe.lon };
};

export const getRegionRepresentativeCoordinates = (region: PlannerRegion): HomeCoordinates => {
  return REGION_REPRESENTATIVE_COORDINATES[region] || REGION_REPRESENTATIVE_COORDINATES[DEFAULT_PLANNER_REGION];
};

export const coordinatesMatch = (
  left: HomeCoordinates | null | undefined,
  right: HomeCoordinates | null | undefined,
  tolerance = 0.0001
): boolean => Boolean(
  left &&
  right &&
  Math.abs(left.lat - right.lat) < tolerance &&
  Math.abs(left.lon - right.lon) < tolerance
);

export const getStoredHomeLocationState = (): HomeLocationState => {
  if (typeof window === 'undefined') {
    return { mode: 'idle', region: null, source: null, coordinates: null };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { mode: 'idle', region: null, source: null, coordinates: null };
    }

    const parsed = JSON.parse(raw) as Partial<HomeLocationState>;
    const region = clampRegion(parsed.region);
    if (parsed.source === 'geolocation') {
      return {
        mode: region ? 'manual' : 'idle',
        region,
        source: region ? 'manual' : null,
        coordinates: null
      };
    }

    return {
      mode: parsed.mode === 'granted' || parsed.mode === 'manual' || parsed.mode === 'denied' ? parsed.mode : 'idle',
      region,
      source: parsed.source === 'manual' ? parsed.source : null,
      coordinates: clampCoordinates(parsed.coordinates)
    };
  } catch {
    return { mode: 'idle', region: null, source: null, coordinates: null };
  }
};

export const storeHomeLocationState = (state: HomeLocationState): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Precise device coordinates are session-only. Persist the inferred region
  // so the next visit remains useful, but treat it as a regional reference.
  const persistedState: HomeLocationState = state.source === 'geolocation'
    ? { mode: 'manual', region: state.region, source: 'manual', coordinates: null }
    : state;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(persistedState));
};

export const clearHomeLocationState = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(STORAGE_KEY);
};

export const inferPlannerRegionFromCoordinates = (lat: number, lon: number): PlannerRegion | null => {
  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lon) ||
    lat < -90 ||
    lat > 90 ||
    lon < -180 ||
    lon > 180
  ) {
    return null;
  }

  const point = { lat, lon };
  const closestByRegion = new Map<PlannerRegion, number>();

  REVIEWED_COASTAL_ANCHORS.forEach((anchor) => {
    const distance = distanceInKm(point, anchor);
    const previousDistance = closestByRegion.get(anchor.region);
    if (previousDistance == null || distance < previousDistance) {
      closestByRegion.set(anchor.region, distance);
    }
  });

  const rankedRegions = Array.from(closestByRegion.entries())
    .map(([region, distance]) => ({ region, distance }))
    .sort((left, right) => left.distance - right.distance);
  const closest = rankedRegions[0];
  const runnerUp = rankedRegions[1];

  if (!closest || closest.distance > REVIEWED_COASTAL_RADIUS_KM) {
    return null;
  }

  if (runnerUp && runnerUp.distance - closest.distance < MIN_REGION_SEPARATION_KM) {
    return null;
  }

  return closest.region;
};

export const requestUserHomeLocation = async (): Promise<ResolvedHomeLocation> => {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    throw new Error(String(i18next.t('codex:locationAwareHome.geolocationNotSupported')));
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coordinates = {
          lat: position.coords.latitude,
          lon: position.coords.longitude
        };

        resolve({
          region: inferPlannerRegionFromCoordinates(coordinates.lat, coordinates.lon),
          coordinates
        });
      },
      (error) => {
        reject(new Error(error.message || String(i18next.t('codex:locationAwareHome.locationUnavailable'))));
      },
      {
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 1000 * 60 * 60 * 12
      }
    );
  });
};

export const requestUserRegion = async (): Promise<PlannerRegion> => {
  const resolved = await requestUserHomeLocation();
  if (!resolved.region) {
    throw new Error(String(i18next.t('codex:locationAwareHome.locationUnavailable')));
  }

  return resolved.region;
};
